import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, MapPin, CheckCheck, Trash2, Map as MapIcon, QrCode, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr, fileUrl } from "../lib/api";
import { queueInspection } from "../lib/offline";
import { PageHeader } from "../components/ModulePage";
import { Button } from "../components/ui/button";
import { RagBadge } from "../components/Badges";
import { SignaturePad } from "../components/SignaturePad";
import { PhotoUpload } from "../components/PhotoUpload";
import { ItemRow } from "./InspectionRunner";
import { QrScanner } from "../components/QrScanner";
import { PlanMap } from "../components/PlanMap";
import { fmtDateTime } from "../lib/api";

const CheckIn = ({ area, checkin, onCheckIn }) => {
  const [scan, setScan] = useState(false);
  const [code, setCode] = useState("");
  const onResult = useCallback((text) => {
    setScan(false);
    const t = text.trim().toUpperCase();
    if (t === `AREA:${area.id}`.toUpperCase() || t === `AREA:${area.code}`.toUpperCase()) onCheckIn({ method: "qr" });
    else toast.error(`This QR code is not for ${area.name}`);
  }, [area, onCheckIn]);
  if (checkin)
    return (
      <div className="flex items-center gap-2 rounded-md border border-emerald-600/50 bg-emerald-600/10 px-4 py-3 text-sm text-emerald-200" data-testid="area-checked-in">
        <ShieldCheck className="h-4 w-4" />Checked in {fmtDateTime(checkin.at)} - {checkin.method === "qr" ? "verified by QR scan" : checkin.method === "code" ? "label code entered" : `manual (${checkin.reason})`}
      </div>
    );
  const typed = () => {
    if (code.trim().toUpperCase() === area.code.toUpperCase()) onCheckIn({ method: "code" });
    else toast.error("Code does not match this area's label");
  };
  const manual = () => {
    const reason = window.prompt("Reason for manual check-in (e.g. label missing / damaged)");
    if (reason) onCheckIn({ method: "manual", reason });
  };
  return (
    <div className="space-y-3 rounded-md border border-amber-500/50 bg-amber-500/10 p-4" data-testid="area-checkin-panel">
      <p className="text-sm font-semibold text-amber-100">Check in to {area.name} to start this area</p>
      <div className="grid gap-2 sm:grid-cols-2">
        <Button onClick={() => setScan(!scan)} className="tap bg-rose-600 hover:bg-rose-700" data-testid="checkin-scan-btn"><QrCode className="mr-2 h-4 w-4" />{scan ? "Close scanner" : "Scan area QR"}</Button>
        <Button variant="outline" onClick={manual} className="tap border-slate-600 bg-transparent" data-testid="checkin-manual-btn">Manual check-in</Button>
      </div>
      {scan && <QrScanner onResult={onResult} />}
      <div className="flex gap-2">
        <input className="field h-10" placeholder={`Or type label code (${area.code.replace(/[A-Z0-9]/g, "#")})`} value={code} onChange={(e) => setCode(e.target.value)} data-testid="checkin-code-input" />
        <Button variant="outline" className="h-10 border-slate-600 bg-transparent" onClick={typed} data-testid="checkin-code-btn">Verify</Button>
      </div>
    </div>
  );
};

const RouteMap = ({ steps, idx, done, go }) => {
  const pinned = steps.map((s, i) => ({ s, i })).filter(({ s }) => s.area.pin);
  if (!pinned.length) return <p className="panel mb-4 p-4 text-sm text-slate-500" data-testid="route-map-empty">No areas on this route are pinned on a site plan yet (Site Setup - Site plans).</p>;
  const plan = pinned[0].s.area.pin.plan;
  const pins = pinned.filter(({ s }) => s.area.pin.plan === plan).map(({ s, i }) => ({
    id: s.area.id, code: s.area.code, name: s.area.name, label: String.fromCharCode(65 + (i % 26)), x: s.area.pin.x, y: s.area.pin.y, state: i === idx ? "current" : done(i) ? "done" : "todo", i,
  }));
  return <div className="mb-4"><PlanMap plan={plan} pins={pins} onPinClick={(p) => go(p.i)} testid="route-map" /></div>;
};

const k = (areaId, assetId, q) => `${areaId}|${assetId || "area"}|${q}`;
const stepKeys = (st) => [...st.assets.flatMap((a) => a.questions.map((q) => k(st.area.id, a.id, q))), ...st.area_questions.map((q) => k(st.area.id, null, q))];
const photoKeys = (st) => [...st.assets.flatMap((a) => (a.photo_questions || []).map((q) => k(st.area.id, a.id, q))), ...(st.area_photo_questions || []).map((q) => k(st.area.id, null, q))];

const RouteBar = ({ steps, idx, vals, go }) => (
  <div className="mb-4 flex gap-1 overflow-x-auto pb-1" data-testid="pathway-route-bar">
    {[...steps.map((s) => s.area.name), "Review & sign"].map((n, i) => {
      const done = i < steps.length && stepKeys(steps[i]).every((x) => vals[x]?.result);
      return (
        <button key={i} onClick={() => go(i)} data-testid={`route-step-${i}`}
          className={`flex shrink-0 items-center gap-1 rounded-md border px-3 py-2 text-xs ${i === idx ? "border-rose-500 bg-rose-600 text-white" : done ? "border-emerald-600 bg-emerald-600/15 text-emerald-200" : "border-slate-700 text-slate-400"}`}>
          <span className="font-mono">{i < steps.length ? String.fromCharCode(65 + (i % 26)) : "✓"}</span>{n}
        </button>
      );
    })}
  </div>
);

const AreaStep = ({ step, vals, setVal, photos, setPhotos }) => (
  <div className="space-y-4" data-testid={`pathway-area-${step.area.code}`}>
    <div className="panel p-4">
      <p className="eyebrow flex items-center gap-1 text-rose-300"><MapPin className="h-3 w-3" />{step.area.code}</p>
      <h2 className="font-display text-2xl font-bold uppercase">{step.area.name}</h2>
      {step.area.description && <p className="text-sm text-slate-400">{step.area.description}</p>}
      {step.area.photos?.length > 0 && (
        <div className="mt-3 flex gap-2 overflow-x-auto">
          {step.area.photos.map((p) => <a key={p} href={fileUrl(p)} target="_blank" rel="noreferrer"><img src={fileUrl(p)} alt={step.area.name} className="h-28 w-40 shrink-0 rounded object-cover" /></a>)}
        </div>
      )}
    </div>
    {step.assets.map((a, n) => (
      <div key={a.id} className="panel px-4 py-2" data-testid={`pathway-asset-${a.asset_code}`}>
        <div className="flex items-center justify-between pt-2">
          <h3 className="font-display text-lg font-bold uppercase"><span className="mr-2 font-mono text-sm text-cyan-300">{n + 1}. {a.asset_code}</span>{a.name}</h3>
          <RagBadge status={a.rag} />
        </div>
        {a.type && <p className="eyebrow text-[10px]">{a.type}</p>}
        {a.questions.map((q) => <ItemRow key={q} prefix={`pw-${a.asset_code}`} photoRequired={(a.photo_questions || []).includes(q)} sec="q" item={q} val={vals[k(step.area.id, a.id, q)] || {}} setVal={(v) => setVal(k(step.area.id, a.id, q), v)} />)}
      </div>
    ))}
    {!step.assets.length && <p className="panel p-4 text-sm text-slate-500">No assets assigned to this area yet - general area checks only.</p>}
    <div className="panel px-4 py-2">
      <h3 className="font-display pt-2 text-lg font-bold uppercase text-rose-300">General area checks</h3>
      {step.area_questions.map((q) => <ItemRow key={q} prefix={`pw-area-${step.area.code}`} photoRequired={(step.area_photo_questions || []).includes(q)} sec="q" item={q} val={vals[k(step.area.id, null, q)] || {}} setVal={(v) => setVal(k(step.area.id, null, q), v)} />)}
    </div>
    <div className="panel space-y-2 p-4">
      <p className="eyebrow">Area photo evidence</p>
      <PhotoUpload value={photos} onChange={setPhotos} testid={`pathway-photos-${step.area.code}`} />
    </div>
  </div>
);

export default function PathwayRunner() {
  const { id } = useParams();
  const nav = useNavigate();
  const draftKey = `eps_draft_${id}`;
  const [run, setRun] = useState(() => JSON.parse(localStorage.getItem(`eps_run_${id}`) || "null"));
  const [draft, setDraft] = useState(() => JSON.parse(localStorage.getItem(draftKey) || "null") || { idx: 0, vals: {}, photos: {}, notes: "", checkins: {} });
  const [showMap, setShowMap] = useState(false);
  const [signature, setSignature] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/pathways/${id}/run`).then((r) => { localStorage.setItem(`eps_run_${id}`, JSON.stringify(r.data)); setRun(r.data); }).catch(() => {});
  }, [id]);
  const update = useCallback((patch) => setDraft((d) => {
    const nd = { ...d, ...patch };
    localStorage.setItem(draftKey, JSON.stringify(nd));
    return nd;
  }), [draftKey]);

  if (!run) return <p className="text-slate-400" data-testid="pathway-loading">Loading pathway... (open it once while online to use offline)</p>;
  const { steps, pathway } = run;
  const { idx, vals, photos, notes } = draft;
  const checkins = draft.checkins || {};
  const checkIn = (area) => (c) => update({ checkins: { ...checkins, [area.id]: { area_id: area.id, area_name: area.name, at: new Date().toISOString(), ...c } } });
  const setVal = (key, v) => update({ vals: { ...vals, [key]: v } });
  const step = steps[idx];
  const complete = (i) => !!checkins[steps[i].area.id] && stepKeys(steps[i]).every((x) => vals[x]?.result) && photoKeys(steps[i]).every((x) => vals[x]?.photos?.length);
  const passArea = () => update({ vals: { ...vals, ...Object.fromEntries(stepKeys(step).filter((x) => !vals[x]?.result).map((x) => [x, { result: "pass" }])) } });
  const fails = (i) => stepKeys(steps[i]).filter((x) => vals[x]?.result === "fail").length;
  const allDone = steps.every((_, i) => complete(i));

  const submit = async () => {
    const results = steps.flatMap((s) => [
      ...s.assets.flatMap((a) => a.questions.map((q) => ({ section: `${s.area.name} › ${a.asset_code} ${a.name}`, item: q, ...vals[k(s.area.id, a.id, q)], area_id: s.area.id, area_name: s.area.name, asset_id: a.id, asset_name: `${a.asset_code} ${a.name}`, asset_kind: a.kind }))),
      ...s.area_questions.map((q) => ({ section: `${s.area.name} › General`, item: q, ...vals[k(s.area.id, null, q)], area_id: s.area.id, area_name: s.area.name, asset_id: null, asset_name: null })),
    ]);
    const payload = { client_id: crypto.randomUUID(), performed_at: new Date().toISOString(), results, photos: Object.values(photos).flat(), notes, signature, checkins: Object.values(checkins), site_id: pathway.site_id, _endpoint: `/pathways/${id}/submit` };
    setBusy(true);
    try {
      if (!navigator.onLine) throw Object.assign(new Error("offline"), { offline: true });
      const r = await api.post(payload._endpoint, payload);
      toast.success(r.data.work_order_ref ? `Audit saved - defects raised: ${r.data.work_order_ref}` : "Audit saved - all areas passed");
      localStorage.removeItem(draftKey);
      nav("/audits");
    } catch (e) {
      if (e.offline || !e.response) {
        queueInspection(payload);
        localStorage.removeItem(draftKey);
        toast.success("Saved offline - will sync when back online");
        nav("/audits");
      } else toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };
  const discard = () => { localStorage.removeItem(draftKey); setDraft({ idx: 0, vals: {}, photos: {}, notes: "", checkins: {} }); };

  return (
    <div className="mx-auto max-w-3xl" data-testid="pathway-runner">
      <PageHeader eyebrow={`Pathway audit - ${steps.length} areas`} title={pathway.name} description={pathway.description}>
        <Button variant="outline" size="sm" className="border-slate-700 bg-transparent" onClick={() => setShowMap(!showMap)} data-testid="pathway-map-btn"><MapIcon className="mr-1 h-4 w-4" />{showMap ? "Hide map" : "Route map"}</Button>
        <Button variant="ghost" size="sm" onClick={discard} data-testid="pathway-discard-btn"><Trash2 className="mr-1 h-4 w-4" />Restart</Button>
      </PageHeader>
      {showMap && <RouteMap steps={steps} idx={idx} done={complete} go={(i) => { update({ idx: i }); setShowMap(false); }} />}
      <RouteBar steps={steps} idx={idx} vals={vals} go={(i) => update({ idx: i })} />
      {step ? (
        <>
          <div className="sticky top-16 z-20 -mx-4 mb-4 flex items-center justify-between border-b border-slate-800 bg-ink/95 px-4 py-3 backdrop-blur sm:mx-0 sm:rounded-md sm:border">
            <span className="font-mono text-sm" data-testid="pathway-progress">Area {idx + 1} of {steps.length}{fails(idx) ? <span className="text-red-400"> - {fails(idx)} fail</span> : ""}</span>
            <Button variant="outline" size="sm" disabled={!checkins[step.area.id]} className="h-10 border-slate-700 bg-transparent" onClick={passArea} data-testid="pathway-pass-area-btn"><CheckCheck className="mr-1 h-4 w-4" />Rest of area OK</Button>
          </div>
          <div className="mb-4"><CheckIn key={step.area.id} area={step.area} checkin={checkins[step.area.id]} onCheckIn={checkIn(step.area)} /></div>
          <fieldset disabled={!checkins[step.area.id]} className={checkins[step.area.id] ? "" : "pointer-events-none opacity-50"}>
            <AreaStep step={step} vals={vals} setVal={setVal} photos={photos[step.area.id] || []} setPhotos={(p) => update({ photos: { ...photos, [step.area.id]: p } })} />
          </fieldset>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <Button variant="outline" disabled={idx === 0} onClick={() => update({ idx: idx - 1 })} className="h-12 border-slate-700 bg-transparent" data-testid="pathway-back-btn"><ChevronLeft className="mr-1 h-4 w-4" />Back</Button>
            <Button disabled={!complete(idx)} onClick={() => { update({ idx: idx + 1 }); window.scrollTo(0, 0); }} className="h-12 bg-rose-600 hover:bg-rose-700" data-testid="pathway-next-btn">
              {complete(idx) ? (idx + 1 < steps.length ? `Next: ${steps[idx + 1].area.name}` : "Review") : !checkins[step.area.id] ? "Check in first" : stepKeys(step).every((x) => vals[x]?.result) ? "Add required photos" : "Answer all checks"}<ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </>
      ) : (
        <div className="panel space-y-4 p-4" data-testid="pathway-review">
          <h2 className="font-display text-2xl font-bold uppercase">Review & sign</h2>
          {steps.map((s, i) => (
            <div key={s.area.id} className="flex items-center justify-between border-b border-slate-800 py-2 text-sm">
              <span>{String.fromCharCode(65 + (i % 26))}. {s.area.name}<span className="block text-[11px] text-slate-500">{checkins[s.area.id] ? `Check-in: ${checkins[s.area.id].method === "qr" ? "QR verified" : checkins[s.area.id].method}` : "Not checked in"}</span></span>
              <RagBadge status={!complete(i) ? "amber" : fails(i) ? "red" : "green"} label={!complete(i) ? "Incomplete" : fails(i) ? `${fails(i)} fail` : "Pass"} />
            </div>
          ))}
          <textarea className="field h-20 py-2" placeholder="Audit notes" value={notes} onChange={(e) => update({ notes: e.target.value })} data-testid="pathway-notes" />
          <p className="eyebrow">Auditor signature *</p>
          <SignaturePad onChange={setSignature} testid="pathway-signature" />
          <Button onClick={submit} disabled={busy || !allDone || !signature} className="h-12 w-full bg-rose-600 text-base hover:bg-rose-700" data-testid="pathway-submit-btn">
            {!allDone ? "Complete all areas first" : !signature ? "Sign to submit" : busy ? "Submitting..." : "Submit pathway audit"}
          </Button>
        </div>
      )}
    </div>
  );
}
